import { z } from 'zod'
import { Button } from '@/components/ui/button'
import { toast } from 'sonner'
import type { Message } from '@/types'
import { Loader2, Save, Send } from 'lucide-react'
import { NodeForm } from '@/components/nodes/NodeForm'
import { nodeFormConfigs } from '@/lib/config/node-form-configs'

const _messageSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  description: z.string().min(1, 'Message description is required'),
  workspaceId: z.string().optional(),
  directMessage: z.boolean(),
  userIds: z.array(z.string()),
  projectIds: z.array(z.string()).optional(),
  taskIds: z.array(z.string()).optional(),
  documentIds: z.array(z.string()).optional(),
})

export type MessageFormData = z.infer<typeof _messageSchema>

interface MessageFormProps {
  mode: 'create' | 'edit'
  initialData?: Partial<Message>
  onSubmit: (data: MessageFormData, isDraft: boolean) => Promise<void>
  onCancel: () => void
  cancelText?: string
  loading?: boolean
  primaryActionLoading?: boolean
  secondaryActionLoading?: boolean
  onDirtyChange?: (isDirty: boolean) => void
}

export function MessageForm({
  mode,
  initialData,
  onSubmit,
  onCancel,
  cancelText,
  loading = false,
  primaryActionLoading = false,
  secondaryActionLoading = false,
  onDirtyChange,
}: MessageFormProps) {
  // Convert Message data to NodeForm compatible format
  const convertToNodeFormData = (messageData?: Partial<Message>) => {
    if (!messageData) return {}

    return {
      name: messageData.name || '',
      description: messageData.description || '',
      workspaceId: messageData.workspace?.id || '',
      directMessage: messageData.directMessage || false,
      userIds: messageData.users?.map((u) => u.id) || [],
      projectIds: messageData.projects?.map((p) => p.id) || [],
      taskIds: messageData.tasks?.map((t) => t.id) || [],
      documentIds: messageData.documents?.map((d) => d.id) || [],
    }
  }

  const handleNodeFormSubmit = async (data: Record<string, unknown>) => {
    const messageData = data as MessageFormData

    // Check if this was triggered by the secondary action button
    const form = document.querySelector('form')
    const isSecondaryAction = form?.getAttribute('data-secondary-action') === 'true'

    // Clear the attribute for next time
    if (form) {
      form.removeAttribute('data-secondary-action')
    }

    // Determine if this is a draft based on the action
    let isDraft: boolean
    if (isSecondaryAction) {
      // Secondary action: Save as draft (create) or Send message (edit)
      isDraft = mode === 'create' ? true : false
    } else {
      // Primary action: Send message (create) or Update draft (edit)
      isDraft = mode === 'create' ? false : true
    }

    await handleSubmitWithValidation(messageData, isDraft)
  }

  const handleSubmitWithValidation = async (messageData: MessageFormData, isDraft: boolean) => {
    // Validation logic from original MessageForm
    if (!isDraft && !messageData.directMessage && messageData.userIds.length === 0) {
      toast.error(
        'Please add at least one recipient to send this message, or save as draft instead.'
      )
      return
    }

    if (!isDraft && messageData.directMessage && messageData.userIds.length !== 1) {
      toast.error(
        'Direct messages must have exactly one recipient. Please select one recipient or save as draft instead.'
      )
      return
    }

    await onSubmit(messageData, isDraft)
  }

  const handleSecondaryAction = () => {
    // Secondary action: Save as draft (create) or Send message (edit)
    // Set the draft mode and trigger form submission
    // We need to temporarily store this and trigger submission
    // Since we can't pass parameters to form submission, we'll use a different approach
    const form = document.querySelector('form')
    if (form) {
      // Add a data attribute to indicate this is a secondary action
      form.setAttribute('data-secondary-action', 'true')
      form.requestSubmit()
    }
  }

  const customActions = (
    <Button
      type="button"
      variant={mode === 'create' ? 'outline' : 'default'}
      onClick={handleSecondaryAction}
      disabled={loading}
      className="flex-1 w-full"
    >
      {loading && secondaryActionLoading ? (
        <>
          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          {mode === 'create' ? 'Saving...' : 'Sending...'}
        </>
      ) : mode === 'create' ? (
        <>
          <Save className="mr-2 h-4 w-4" />
          Save as Draft
        </>
      ) : (
        <>
          <Send className="mr-2 h-4 w-4" />
          Send Message
        </>
      )}
    </Button>
  )

  return (
    <NodeForm
      mode={mode}
      config={nodeFormConfigs.message}
      initialData={convertToNodeFormData(initialData)}
      initialUsers={initialData?.users || []}
      initialProjects={initialData?.projects || []}
      initialDependencies={initialData?.tasks || []}
      onSubmit={handleNodeFormSubmit}
      onCancel={onCancel}
      loading={loading}
      title="Message Details"
      description={
        mode === 'create'
          ? 'Send a message to team members or save as draft'
          : 'Update the draft or send the message'
      }
      submitText={
        mode === 'create'
          ? loading && primaryActionLoading
            ? 'Sending...'
            : 'Send Message'
          : loading && primaryActionLoading
            ? 'Saving...'
            : 'Update Draft'
      }
      cancelText={cancelText}
      customActions={customActions}
      onSecondaryAction={handleSecondaryAction}
      onDirtyChange={onDirtyChange}
    />
  )
}
